import Cookies from 'js-cookie';

const COOKIE_OPTIONS: Cookies.CookieAttributes = {
  secure: window.location.protocol === 'https:',
  sameSite: 'Lax',
  expires: 7,
};

const LONG_COOKIE_OPTIONS: Cookies.CookieAttributes = {
  ...COOKIE_OPTIONS,
  expires: 30,
};

const KEYS = {
  ACCESS_TOKEN: 'access_token',
  REFRESH_TOKEN: 'refresh_token',
  ID_TOKEN: 'id_token',
  USER: 'cc_user',
  REFRESH_FAIL_COUNT: 'refresh_failure_count',
} as const;

/** Check if cookies are available */
function cookiesAvailable(): boolean {
  try {
    Cookies.set('__cookie_test', '1');
    const result = Cookies.get('__cookie_test') === '1';
    Cookies.remove('__cookie_test');
    return result;
  } catch {
    return false;
  }
}

const useCookies = cookiesAvailable();

function get(key: string): string | null {
  if (useCookies) {
    return Cookies.get(key) || null;
  }
  return localStorage.getItem(key);
}

function set(key: string, value: string, long = false): void {
  if (useCookies) {
    Cookies.set(key, value, long ? LONG_COOKIE_OPTIONS : COOKIE_OPTIONS);
  }
  localStorage.setItem(key, value);
}

function remove(key: string): void {
  if (useCookies) {
    Cookies.remove(key);
  }
  localStorage.removeItem(key);
}

export const tokenStorage = {
  getAccessToken: () => get(KEYS.ACCESS_TOKEN),
  setAccessToken: (token: string) => set(KEYS.ACCESS_TOKEN, token),
  removeAccessToken: () => remove(KEYS.ACCESS_TOKEN),

  getRefreshToken: () => get(KEYS.REFRESH_TOKEN),
  setRefreshToken: (token: string) => set(KEYS.REFRESH_TOKEN, token, true),
  removeRefreshToken: () => remove(KEYS.REFRESH_TOKEN),

  getIdToken: () => get(KEYS.ID_TOKEN),
  setIdToken: (token: string) => set(KEYS.ID_TOKEN, token),
  removeIdToken: () => remove(KEYS.ID_TOKEN),

  getUser: (): any | null => {
    const raw = get(KEYS.USER);
    if (!raw) return null;
    try { return JSON.parse(raw); } catch { return null; }
  },
  setUser: (user: any) => set(KEYS.USER, JSON.stringify(user)),
  removeUser: () => remove(KEYS.USER),

  getRefreshFailCount: (): number => {
    const val = get(KEYS.REFRESH_FAIL_COUNT);
    return val ? parseInt(val, 10) : 0;
  },
  setRefreshFailCount: (count: number) => set(KEYS.REFRESH_FAIL_COUNT, String(count)),
  removeRefreshFailCount: () => remove(KEYS.REFRESH_FAIL_COUNT),

  clearAll: () => {
    Object.values(KEYS).forEach((key) => {
      if (useCookies) Cookies.remove(key);
      localStorage.removeItem(key);
    });
    // Also clear legacy keys
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('id_token');
    localStorage.removeItem('user');
    localStorage.removeItem('refresh_failure_count');
  },

  setTokens: (accessToken: string, refreshToken: string, idToken: string) => {
    set(KEYS.ACCESS_TOKEN, accessToken);
    set(KEYS.REFRESH_TOKEN, refreshToken, true);
    set(KEYS.ID_TOKEN, idToken);
  },
};
